/**
 * Create / edit announcement modal (HR-only, enforced by the server too).
 *
 * Keeps targeting simple: "all" vs. pick-specific for departments and for
 * roles. Attachments are entered as a URL paste — the platform has no
 * direct-upload helper yet, so we defer that to 7B and let HR link to
 * whatever object store they already use.
 */
import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import {
  createAnnouncement,
  updateAnnouncement,
  type Announcement,
  type AnnouncementCategory,
  type AnnouncementPriority,
} from "@/api/announcements";
import { listDepartments } from "@/api/organizations";
import type { DepartmentFull } from "@/types/organization";
import { useToast } from "@/hooks/useToast";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  existing?: Announcement | null;
}

const CATEGORIES: { value: AnnouncementCategory; label: string }[] = [
  { value: "general", label: "General" },
  { value: "policy", label: "Policy" },
  { value: "event", label: "Event" },
  { value: "welcome", label: "Welcome" },
  { value: "urgent", label: "Urgent" },
];
const PRIORITIES: { value: AnnouncementPriority; label: string }[] = [
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];
const ROLE_OPTIONS = ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "EMPLOYEE"];

function toLocalDatetimeInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalDatetimeInput(v: string): string | null {
  if (!v) return null;
  return new Date(v).toISOString();
}

export function AnnouncementFormModal({ isOpen, onClose, onSaved, existing }: Props) {
  const { addToast } = useToast();
  const [departments, setDepartments] = useState<DepartmentFull[]>([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState<AnnouncementCategory>("general");
  const [priority, setPriority] = useState<AnnouncementPriority>("normal");
  const [isPinned, setIsPinned] = useState(false);
  const [deptMode, setDeptMode] = useState<"all" | "some">("all");
  const [deptIds, setDeptIds] = useState<string[]>([]);
  const [roleMode, setRoleMode] = useState<"all" | "some">("all");
  const [roles, setRoles] = useState<string[]>([]);
  const [scheduleMode, setScheduleMode] = useState<"now" | "later">("now");
  const [scheduledFor, setScheduledFor] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentName, setAttachmentName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      listDepartments().then(setDepartments).catch(() => {});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (existing) {
      setTitle(existing.title);
      setBody(existing.body);
      setCategory(existing.category);
      setPriority(existing.priority);
      setIsPinned(existing.is_pinned);
      setDeptMode(existing.target_departments?.length ? "some" : "all");
      setDeptIds(existing.target_departments ?? []);
      setRoleMode(existing.target_roles?.length ? "some" : "all");
      setRoles(existing.target_roles ?? []);
      setScheduleMode(existing.scheduled_for && !existing.published_at ? "later" : "now");
      setScheduledFor(toLocalDatetimeInput(existing.scheduled_for));
      setExpiresAt(toLocalDatetimeInput(existing.expires_at));
      setAttachmentUrl(existing.attachment_url ?? "");
      setAttachmentName(existing.attachment_name ?? "");
    } else {
      setTitle("");
      setBody("");
      setCategory("general");
      setPriority("normal");
      setIsPinned(false);
      setDeptMode("all");
      setDeptIds([]);
      setRoleMode("all");
      setRoles([]);
      setScheduleMode("now");
      setScheduledFor("");
      setExpiresAt("");
      setAttachmentUrl("");
      setAttachmentName("");
    }
  }, [isOpen, existing]);

  const submit = async () => {
    if (!title.trim() || !body.trim()) {
      addToast("error", "Title and body are required");
      return;
    }
    setSaving(true);
    const payload = {
      title: title.trim(),
      body,
      category,
      priority,
      is_pinned: isPinned,
      target_departments: deptMode === "some" ? deptIds : null,
      target_roles: roleMode === "some" ? roles : null,
      scheduled_for: scheduleMode === "later" ? fromLocalDatetimeInput(scheduledFor) : null,
      expires_at: fromLocalDatetimeInput(expiresAt),
      attachment_url: attachmentUrl.trim() || null,
      attachment_name: attachmentName.trim() || null,
    };
    try {
      if (existing) {
        await updateAnnouncement(existing.id, payload);
        addToast("success", "Announcement updated");
      } else {
        await createAnnouncement({
          ...payload,
          publish_now: scheduleMode === "now",
        });
        addToast(
          "success",
          scheduleMode === "now" ? "Announcement published" : "Announcement scheduled"
        );
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      // Surface the real backend detail (FastAPI puts 4xx errors in
      // response.data.detail). Without this the user just sees a
      // generic toast and has no idea whether it's a validation error,
      // a permissions issue, or the server being down.
      const ax = err as {
        response?: { status?: number; data?: { detail?: unknown } };
        message?: string;
        code?: string;
      };
      const raw = ax?.response?.data?.detail;
      let message = "Failed to save announcement";
      if (typeof raw === "string" && raw.trim()) {
        message = raw;
      } else if (Array.isArray(raw)) {
        // Pydantic v2 validation errors come back as an array of
        // {loc, msg, type} objects — the first one is usually enough.
        const first = raw[0] as { msg?: string; loc?: unknown[] } | undefined;
        if (first?.msg) {
          const field = Array.isArray(first.loc)
            ? first.loc.filter((x) => x !== "body").join(".")
            : "";
          message = field ? `${field}: ${first.msg}` : first.msg;
        }
      } else if (raw && typeof raw === "object") {
        message = JSON.stringify(raw);
      } else if (ax?.response?.status) {
        message = `Failed to save announcement (HTTP ${ax.response.status})`;
      } else if (ax?.code === "ERR_NETWORK") {
        message = "Network error — check your connection and try again";
      } else if (ax?.message) {
        message = ax.message;
      }
      addToast("error", message);
    } finally {
      setSaving(false);
    }
  };

  const toggleInList = (list: string[], setList: (v: string[]) => void, value: string) => {
    setList(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={existing ? "Edit announcement" : "New announcement"}
      size="xl"
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={300}
        />
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Body</label>
          <textarea
            rows={8}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="Write your announcement. Plain text — paragraphs and links are fine."
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value as AnnouncementCategory)}
            options={CATEGORIES}
          />
          <Select
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as AnnouncementPriority)}
            options={PRIORITIES}
          />
          <label className="flex items-end gap-2 pb-1 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={isPinned}
              onChange={(e) => setIsPinned(e.target.checked)}
              className="rounded border-border"
            />
            Pin to top of feed
          </label>
        </div>

        {/* Targeting */}
        <div className="rounded-lg border border-border p-3 space-y-3">
          <p className="text-sm font-medium text-text-primary">Who sees this?</p>
          <div className="flex items-center gap-4 text-sm text-text-primary">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={deptMode === "all"}
                onChange={() => setDeptMode("all")}
              />
              All departments
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={deptMode === "some"}
                onChange={() => setDeptMode("some")}
              />
              Specific departments
            </label>
          </div>
          {deptMode === "some" && (
            <div className="flex flex-wrap gap-2">
              {departments.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => toggleInList(deptIds, setDeptIds, d.id)}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    deptIds.includes(d.id)
                      ? "bg-accent text-white border-accent"
                      : "border-border text-text-primary hover:border-accent/50"
                  }`}
                >
                  {d.name}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-4 text-sm text-text-primary pt-2 border-t border-border">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={roleMode === "all"}
                onChange={() => setRoleMode("all")}
              />
              All roles
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={roleMode === "some"}
                onChange={() => setRoleMode("some")}
              />
              Specific roles
            </label>
          </div>
          {roleMode === "some" && (
            <div className="flex flex-wrap gap-2">
              {ROLE_OPTIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggleInList(roles, setRoles, r)}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    roles.includes(r)
                      ? "bg-accent text-white border-accent"
                      : "border-border text-text-primary hover:border-accent/50"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Schedule */}
        {!existing && (
          <div className="rounded-lg border border-border p-3 space-y-3">
            <p className="text-sm font-medium text-text-primary">When?</p>
            <div className="flex items-center gap-4 text-sm text-text-primary">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={scheduleMode === "now"}
                  onChange={() => setScheduleMode("now")}
                />
                Publish now
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={scheduleMode === "later"}
                  onChange={() => setScheduleMode("later")}
                />
                Schedule for later
              </label>
            </div>
            {scheduleMode === "later" && (
              <Input
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
              />
            )}
          </div>
        )}

        <Input
          type="datetime-local"
          label="Expires at (optional)"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label="Attachment URL (optional)"
            value={attachmentUrl}
            onChange={(e) => setAttachmentUrl(e.target.value)}
            placeholder="https://…"
          />
          <Input
            label="Attachment name"
            value={attachmentName}
            onChange={(e) => setAttachmentName(e.target.value)}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-border">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={saving}>
          {saving ? "Saving…" : existing ? "Save changes" : "Publish"}
        </Button>
      </div>
    </Modal>
  );
}
